import { Observable } from "rxjs";
import { collections } from "services";
import { db, firebase } from "../../firebase";
import { buildServicePayload } from "../../helpers/appointmentService.helper";

export async function addNewBusiness(business) {
  const currentUser = firebase.auth().currentUser;

  const token = await currentUser.getIdToken(true);
  return fetch(`/app/locations`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      Accepts: "application/json"
    },
    body: JSON.stringify(business)
  })
    .then(response => {
      if (!response.ok || (response.status > 200 && response.status < 600)) {
        throw new Error(response.statusText);
      }
      return response.json();
    })
    .catch(error => {
      console.error("error triggering addNewBusiness function", error);
      throw error;
    });
}

export async function deleteBusiness(businessId, moveUsersBusinessId) {
  const currentUser = firebase.auth().currentUser;

  const token = await currentUser.getIdToken(true);
  return fetch(`/app/locations/delete?id=${businessId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      Accepts: "application/json"
    },
    body: JSON.stringify(moveUsersBusinessId)
  })
    .then(response => {
      if (!response.ok || (response.status > 200 && response.status < 600)) {
        throw new Error(response.status);
      }
      return response.json();
    })
    .catch(error => {
      console.error("error triggering deleteBusiness function", error);
      throw error;
    });
}

export function loadBusinessesByAccountId(user, regions) {
  // `regions` is retained in the signature for call-site compatibility
  // (Admin.jsx passes the live region list). Access scoping happens
  // server-side via /app/locations/get, so the FE no longer resolves grants
  // locally.
  return new Observable(subscriber => {
    let cancelled = false;
    const unsubscribers = [];

    // We hit GET /app/locations/get (token-verified HTTP function) which reads
    // the user's authoritative `access` doc and runs the same resolver the rest
    // of the backend uses. It returns either the "ALL" sentinel (account-wide
    // access — admins / legacy users without an `access` map yet) or the
    // explicit list of accessible business ids. The server also appends the
    // user's own primary businessId so the Admin layout's loading gate never
    // boots on a stripped/empty `access` doc.
    //
    // We then set up a single realtime listener over the whole account
    // (`where('accountId','==',X)`) and filter client-side to whatever the
    // server authorized. "ALL" skips filtering entirely.
    (async () => {
      try {
        const currentUser = firebase.auth().currentUser;
        if (!currentUser) {
          subscriber.next([]);
          return;
        }
        const token = await currentUser.getIdToken(true);
        const response = await fetch(`/app/locations/get`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
            Accepts: "application/json"
          }
        });
        if (!response.ok) {
          console.error("loadBusinessesByAccountId HTTP error", response.status, response.statusText);
          subscriber.next([]);
          return;
        }
        const payload = await response.json();
        if (cancelled) return;

        const accessibleBusinessIds = payload && payload.accessibleBusinessIds;
        const allowAll = accessibleBusinessIds === "ALL";
        const allowed = allowAll ? null : new Set(Array.isArray(accessibleBusinessIds) ? accessibleBusinessIds : []);

        if (!allowAll && allowed.size === 0) {
          subscriber.next([]);
          return;
        }

        const removeListener = db
          .collection(collections.businesses)
          .where("accountId", "==", user.accountId)
          .onSnapshot(
            result => {
              if (cancelled) return;
              const docs = allowAll ? result.docs : result.docs.filter(doc => allowed.has(doc.id));
              subscriber.next(docs);
            },
            error => {
              console.error("loadBusinessesByAccountId onSnapshot error", error);
              subscriber.next([]);
            }
          );
        unsubscribers.push(removeListener);
      } catch (error) {
        console.error("loadBusinessesByAccountId error", error);
        if (!cancelled) subscriber.next([]);
      }
    })();

    return function unsubscribe() {
      cancelled = true;
      unsubscribers.forEach(u => u && u());
    };
  });
}

export function getDashboardBusinessesByAccountId(accountId) {
  return db.collection(collections.businesses).where("accountId", "==", accountId).get();
}

export function getBusinessById(id) {
  return db.collection(collections.businesses).doc(id).get();
}

export function getBusinessSettings(id) {
  return db.collection(collections.businesses).doc(id).get();
}

export function getBusinessResources(id) {
  return db.collection(collections.businesses).doc(id).collection("resources").get();
}

// Also removes any date override scoped to this resource, so no shift doc is left
// pointing at a resource that no longer exists.
export async function deleteBusinessResource(businessId, resourceId, accountId) {
  const resourceRef = db.collection(collections.businesses).doc(businessId).collection("resources").doc(resourceId);

  if (!accountId) {
    return resourceRef.delete();
  }

  // `accountId` equality is required — rules are not filters, so an unscoped list
  // would be denied outright. `resourceId` is filtered here to avoid a new index.
  const shifts = await db
    .collection(collections.businesses)
    .doc(businessId)
    .collection(collections.shifts)
    .where("accountId", "==", accountId)
    .get();

  const batch = db.batch();
  batch.delete(resourceRef);
  shifts?.docs?.filter(doc => doc.data().resourceId === resourceId).forEach(doc => batch.delete(doc.ref));
  return batch.commit();
}

// `clearedFields` is opt-in: callers name the optional fields the user emptied out,
// and only those get deleted. Deliberately not the unconditional treatment
// `notificationText` gets below — a partial payload must never silently wipe a field
// the caller simply didn't manage.
export function updateBusinessResource(businessId, resourceId, resource, clearedFields = []) {
  const update = { ...resource };
  if (!update.notificationText) {
    update.notificationText = firebase.firestore.FieldValue.delete();
  }
  clearedFields.forEach(field => {
    update[field] = firebase.firestore.FieldValue.delete();
  });
  return db
    .collection(collections.businesses)
    .doc(businessId)
    .collection("resources")
    .doc(resourceId)
    .set(update, { merge: true });
}

export function addBusinessResource(businessId, resource) {
  return db.collection(collections.businesses).doc(businessId).collection("resources").add(resource);
}

// --- Appointment services (`businesses/{id}/services`) --------------------------
// Distinct from `settings.services`, which is the waitlist-category list.

function servicesCollection(businessId) {
  return db.collection(collections.businesses).doc(businessId).collection("services");
}

// Every service doc carries `accountId` so the security rule can scope access to the
// owning account. Rules are not filters, so the list query has to repeat the same
// predicate or it is denied wholesale — the constraint `deleteBusinessResource` and
// the aggregates listener already work under.
//
// INVARIANT: any writer of this collection MUST stamp `accountId`. A doc without it is
// orphaned — the list query below skips it, and the rule's `resource.data.accountId`
// comparison errors on the missing field, so it cannot be read, edited, or deleted from
// the admin UI either. The public flow still serves it, because Cloud Functions read
// through the Admin SDK and bypass rules. See plan §16.2 for the alternative that
// removes the invariant entirely.
export function getBusinessServices(businessId, accountId) {
  return servicesCollection(businessId).where("accountId", "==", accountId).get();
}

export function addBusinessService(businessId, service, accountId) {
  const { data } = buildServicePayload(service);
  return servicesCollection(businessId).add({ ...data, accountId });
}

export function updateBusinessService(businessId, serviceId, service, accountId) {
  const { data, clearedFields } = buildServicePayload(service);
  const update = { ...data, accountId };
  // Optional fields the owner emptied out have to be deleted explicitly — under
  // `{ merge: true }` simply omitting them would leave the previous value in place.
  clearedFields.forEach(field => {
    update[field] = firebase.firestore.FieldValue.delete();
  });
  return servicesCollection(businessId).doc(serviceId).set(update, { merge: true });
}

// Also strips the deleted service from every resource that offers it, so no resource
// is left pointing at a service that no longer exists.
export async function deleteBusinessService(businessId, serviceId) {
  const resources = await getBusinessResources(businessId);
  const batch = db.batch();
  batch.delete(servicesCollection(businessId).doc(serviceId));

  resources?.docs
    ?.filter(doc => (doc.data().serviceIds || []).includes(serviceId))
    .forEach(doc => {
      batch.update(doc.ref, {
        serviceIds: firebase.firestore.FieldValue.arrayRemove(serviceId)
      });
    });

  return batch.commit();
}

export async function addOrUpdateBussinessAddress(businessId, address) {
  const currentUser = firebase.auth().currentUser;

  const token = await currentUser.getIdToken(true);
  return fetch(`/app/address`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      Accepts: "application/json"
    },
    body: JSON.stringify({ businessId, location: address })
  })
    .then(async response => {
      if (!response.ok || (response.status > 200 && response.status < 600)) {
        throw new Error((await response.json()).error);
      }
      return response.json();
    })
    .catch(error => {
      console.error("error triggering addOrUpdateBussinessAddress function", error);
      throw error;
    });
}
